import { useState } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import { formatCurrency } from '../../utils/formatters'

const BankAccounts = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterActivo, setFilterActivo] = useState('all')
  const { clientId: filterCliente, setClientId: setFilterCliente, selectDisabled } = useSyncedLocalClientId()
  
  // Formulario
  const [formData, setFormData] = useState({
    cliente: '',
    name: '',
    account_number: '',
    clabe: '',
    bank_name: '',
    account_type: 'CHECKING',
    currency: 'MXN',
    initial_balance: '0.00',
    is_active: true,
    es_cuenta_principal: false,
  })
  
  const [errors, setErrors] = useState({})
  
  // Helper para obtener nombre del cliente
  const getClientName = (client) => {
    if (typeof client === 'string') return client === 'N/A' ? '' : client
    if (client?.razon_social) return client.razon_social
    if (client?.business_name) return client.business_name
    const parts = []
    if (client?.nombre) parts.push(client.nombre)
    if (client?.apellido_paterno) parts.push(client.apellido_paterno)
    if (parts.length > 0) return parts.join(' ')
    return client?.name || ''
  }

  // Obtener clientes para filtro y formulario
  const { data: clients } = useQuery(
    'clients-for-bank-accounts',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )

  // Obtener cuentas bancarias
  const { data: accountsData, isLoading } = useQuery(
    ['bank-accounts', searchTerm, filterActivo, filterCliente],
    async () => {
      const params = {}
      if (searchTerm) params.search = searchTerm
      if (filterActivo !== 'all') params.is_active = filterActivo === 'true'
      if (filterCliente) params.cliente_id = filterCliente
      
      const response = await api.get('/financial/bank-accounts/', { params })
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  const accounts = accountsData || []

  // Filtrar localmente (si es necesario adicional al backend)
  const filteredAccounts = accounts.filter(account => {
    if (filterActivo !== 'all' && account.is_active?.toString() !== filterActivo) return false
    
    // Filtro por cliente
    if (filterCliente) {
      if (filterCliente === 'none') {
        // Filtrar solo cuentas sin cliente (empresa)
        if (account.cliente_id || account.cliente) return false
      } else {
        // Filtrar por cliente específico
        const accountClienteId = account.cliente_id || (account.cliente?.id || (typeof account.cliente === 'number' ? account.cliente : null))
        if (!accountClienteId || accountClienteId.toString() !== filterCliente) return false
      }
    }
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      const name = (account.name || '').toLowerCase()
      const accountNumber = (account.account_number || '').toLowerCase()
      const bankName = (account.bank_name || '').toLowerCase()
      const clienteName = (account.cliente_name || '').toLowerCase()
      if (!name.includes(searchLower) && !accountNumber.includes(searchLower) && !bankName.includes(searchLower) && !clienteName.includes(searchLower)) {
        return false
      }
    }
    return true
  })

  // Mutación para crear/actualizar
  const saveMutation = useMutation(
    async (data) => {
      const payload = {
        ...data,
        cliente_id: data.cliente || null,
        initial_balance: parseFloat(data.initial_balance) || 0,
        is_active: data.is_active !== undefined ? data.is_active : true,
        es_cuenta_principal: data.es_cuenta_principal === true,
      }
      // Remover el campo cliente del payload ya que enviamos cliente_id
      delete payload.cliente
      
      if (selectedAccount) {
        return api.put(`/financial/bank-accounts/${selectedAccount.id}/`, payload)
      } else {
        return api.post('/financial/bank-accounts/', payload)
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bank-accounts')
        queryClient.invalidateQueries('bank-accounts-for-payment')
        queryClient.invalidateQueries('bank-accounts-for-client')
        queryClient.invalidateQueries('clients')
        toast.success(selectedAccount ? 'Cuenta bancaria actualizada correctamente' : 'Cuenta bancaria creada correctamente')
        handleCloseForm()
      },
      onError: (error) => {
        const errorData = error.response?.data
        if (errorData) {
          if (typeof errorData === 'object') {
            setErrors(errorData)
            toast.error('Error al guardar: ' + Object.values(errorData).flat().join(', '))
          } else {
            toast.error('Error: ' + errorData)
          }
        } else {
          toast.error('Error al guardar la cuenta bancaria')
        }
      },
    }
  )

  // Mutación para eliminar
  const deleteMutation = useMutation(
    async (id) => {
      return api.delete(`/financial/bank-accounts/${id}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bank-accounts')
        queryClient.invalidateQueries('bank-accounts-for-payment')
        queryClient.invalidateQueries('bank-accounts-for-client')
        queryClient.invalidateQueries('clients')
        toast.success('Cuenta bancaria eliminada correctamente')
      },
      onError: () => {
        toast.error('Error al eliminar la cuenta bancaria')
      },
    }
  )

  const handleOpenForm = (account = null) => {
    if (account) {
      setSelectedAccount(account)
      // Usar cliente_id si está disponible, sino intentar obtener el ID del objeto cliente
      const clienteId = account.cliente_id || (account.cliente?.id || account.cliente || '')
      setFormData({
        cliente: clienteId ? clienteId.toString() : '',
        name: account.name || '',
        account_number: account.account_number || '',
        clabe: account.clabe || '',
        bank_name: account.bank_name || '',
        account_type: account.account_type || 'CHECKING',
        currency: account.currency || 'MXN',
        initial_balance: account.initial_balance || '0.00',
        is_active: account.is_active !== undefined ? account.is_active : true,
        es_cuenta_principal: account.es_cuenta_principal === true,
      })
    } else {
      setSelectedAccount(null)
      setFormData({
        cliente: '',
        name: '',
        account_number: '',
        clabe: '',
        bank_name: '',
        account_type: 'CHECKING',
        currency: 'MXN',
        initial_balance: '0.00',
        is_active: true,
        es_cuenta_principal: false,
      })
    }
    setErrors({})
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setSelectedAccount(null)
    setFormData({
      cliente: '',
      name: '',
      account_number: '',
      clabe: '',
      bank_name: '',
      account_type: 'CHECKING',
      currency: 'MXN',
      initial_balance: '0.00',
      is_active: true,
      es_cuenta_principal: false,
    })
    setErrors({})
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrors({})
    const clabeDigits = String(formData.clabe || '').replace(/\D/g, '')
    if (clabeDigits && clabeDigits.length !== 18) {
      setErrors({ clabe: 'La CLABE debe tener exactamente 18 dígitos' })
      return
    }
    saveMutation.mutate({ ...formData, clabe: clabeDigits })
  }

  const handleDelete = (account) => {
    if (window.confirm(`¿Estás seguro de eliminar la cuenta bancaria "${account.name}" (${account.account_number})?`)) {
      deleteMutation.mutate(account.id)
    }
  }

  const getAccountTypeLabel = (type) => {
    const types = {
      'CHECKING': 'Cheques',
      'SAVINGS': 'Ahorro',
      'CREDIT': 'Crédito'
    }
    return types[type] || type
  }

  // Estadísticas
  const stats = {
    total: filteredAccounts.length,
    activas: filteredAccounts.filter(a => a.is_active).length,
    inactivas: filteredAccounts.filter(a => !a.is_active).length,
    conCliente: filteredAccounts.filter(a => a.cliente || a.cliente_id).length,
    sinCliente: filteredAccounts.filter(a => !a.cliente && !a.cliente_id).length,
    totalBalance: filteredAccounts.reduce((sum, a) => sum + parseFloat(a.balance || a.initial_balance || 0), 0)
  }

  return (
    <div>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Cuentas Bancarias</h5>
          <Button variant="primary" onClick={() => handleOpenForm()}>
            <i className="bi bi-plus-circle me-1"></i>
            Nueva Cuenta Bancaria
          </Button>
        </Card.Header>
        <Card.Body>
          {/* Estadísticas */}
          <Row className="mb-3">
            <Col md={2}>
              <Card className="text-center border-primary">
                <Card.Body className="py-2">
                  <h5 className="mb-0 text-primary">{stats.total}</h5>
                  <small className="text-muted">Total</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center border-success">
                <Card.Body className="py-2">
                  <h5 className="mb-0 text-success">{stats.activas}</h5>
                  <small className="text-muted">Activas</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center border-secondary">
                <Card.Body className="py-2">
                  <h5 className="mb-0 text-secondary">{stats.inactivas}</h5>
                  <small className="text-muted">Inactivas</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center border-warning">
                <Card.Body className="py-2">
                  <h5 className="mb-0 text-warning">{stats.conCliente}</h5>
                  <small className="text-muted">Con Cliente</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center border-info">
                <Card.Body className="py-2">
                  <h5 className="mb-0 text-info">{stats.sinCliente}</h5>
                  <small className="text-muted">Empresa</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center border-dark">
                <Card.Body className="py-2">
                  <h5 className="mb-0 text-dark">
                    ${stats.totalBalance.toLocaleString('es-MX', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })}
                  </h5>
                  <small className="text-muted">Balance Total</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Filtros */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Buscar por nombre, número, banco, cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Cliente</Form.Label>
                <Form.Select
                  value={filterCliente}
                  onChange={(e) => setFilterCliente(e.target.value)}
                  disabled={selectDisabled}
                >
                  <option value="">Todos los clientes</option>
                  <option value="none">Sin cliente (Empresa)</option>
                  {clients?.map(client => (
                    <option key={client.id} value={client.id}>
                      {getClientName(client)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={filterActivo}
                  onChange={(e) => setFilterActivo(e.target.value)}
                >
                  <option value="all">Todas</option>
                  <option value="true">Activas</option>
                  <option value="false">Inactivas</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Badge bg="secondary" className="ms-auto">
                Total: {stats.total}
              </Badge>
            </Col>
          </Row>

          {/* Tabla */}
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : filteredAccounts.length === 0 ? (
            <Alert variant="info">No se encontraron cuentas bancarias</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Nombre</th>
                  <th>Número de Cuenta</th>
                  <th>CLABE</th>
                  <th>Banco</th>
                  <th>Tipo</th>
                  <th>Moneda</th>
                  <th>Principal (cobros)</th>
                  <th>Balance</th>
                  <th>Estado</th>
                  <th style={{ minWidth: '180px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map(account => (
                  <tr key={account.id}>
                    <td>
                      {account.cliente_name ? (
                        <span>{account.cliente_name}</span>
                      ) : (
                        <span className="text-muted">Empresa</span>
                      )}
                    </td>
                    <td><strong>{account.name}</strong></td>
                    <td>{account.account_number}</td>
                    <td>{account.clabe || <span className="text-muted">—</span>}</td>
                    <td>{account.bank_name}</td>
                    <td>{getAccountTypeLabel(account.account_type)}</td>
                    <td>{account.currency || 'MXN'}</td>
                    <td>
                      {account.es_cuenta_principal ? (
                        <Badge bg="primary">Principal</Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <strong>
                        {formatCurrency(account.balance || account.initial_balance || 0)}
                      </strong>
                    </td>
                    <td>
                      {account.is_active ? (
                        <Badge bg="success">Activa</Badge>
                      ) : (
                        <Badge bg="secondary">Inactiva</Badge>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', minWidth: '200px', textAlign: 'center' }}>
                      <div className="d-flex gap-2 justify-content-center align-items-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenForm(account)}
                          title="Editar cuenta bancaria"
                          style={{ minWidth: '80px' }}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          <span>Editar</span>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(account)}
                          title="Eliminar cuenta bancaria"
                          style={{ minWidth: '80px' }}
                        >
                          <i className="bi bi-trash me-1"></i>
                          <span>Eliminar</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para crear/editar */}
      <Modal show={showForm} onHide={handleCloseForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedAccount ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente (Opcional)</Form.Label>
                  <Form.Select
                    value={formData.cliente}
                    onChange={(e) => setFormData({ ...formData, cliente: e.target.value })}
                    isInvalid={!!errors.cliente}
                  >
                    <option value="">Sin cliente (Cuenta de la empresa)</option>
                    {clients?.map(client => (
                      <option key={client.id} value={client.id}>
                        {getClientName(client)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Si la cuenta pertenece a un cliente específico, selecciónelo. Si está vacío, será una cuenta de la empresa.
                  </Form.Text>
                  {errors.cliente && <Form.Control.Feedback type="invalid">{errors.cliente}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: Cuenta Principal, Cuenta Nómina"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    isInvalid={!!errors.name}
                    required
                  />
                  {errors.name && <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Cuenta *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="1234567890"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    isInvalid={!!errors.account_number}
                    required
                  />
                  {errors.account_number && <Form.Control.Feedback type="invalid">{errors.account_number}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>CLABE (SPEI)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="18 dígitos"
                    value={formData.clabe}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 18)
                      setFormData({ ...formData, clabe: digits })
                    }}
                    maxLength={18}
                    isInvalid={!!errors.clabe}
                  />
                  {errors.clabe && <Form.Control.Feedback type="invalid">{errors.clabe}</Form.Control.Feedback>}
                  <Form.Text className="text-muted">
                    Se imprime en el PDF de facturas del cliente emisor. Opcional.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Banco *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: BBVA, Banorte, HSBC"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    isInvalid={!!errors.bank_name}
                    required
                  />
                  {errors.bank_name && <Form.Control.Feedback type="invalid">{errors.bank_name}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Cuenta *</Form.Label>
                  <Form.Select
                    value={formData.account_type}
                    onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                    isInvalid={!!errors.account_type}
                    required
                  >
                    <option value="CHECKING">Cheques</option>
                    <option value="SAVINGS">Ahorro</option>
                    <option value="CREDIT">Crédito</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="es_cuenta_principal"
                    label="Cuenta principal (para cobros)"
                    checked={formData.es_cuenta_principal === true}
                    onChange={(e) => setFormData({ ...formData, es_cuenta_principal: e.target.checked })}
                  />
                  <Form.Text className="text-muted">Si hay varias principales, se usa la más reciente</Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Moneda *</Form.Label>
                  <Form.Select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    isInvalid={!!errors.currency}
                    required
                  >
                    <option value="MXN">MXN (Peso Mexicano)</option>
                    <option value="USD">USD (Dólar)</option>
                    <option value="EUR">EUR (Euro)</option>
                  </Form.Select>
                  {errors.currency && <Form.Control.Feedback type="invalid">{errors.currency}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Saldo Inicial</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.initial_balance}
                    onChange={(e) => setFormData({ ...formData, initial_balance: e.target.value })}
                    isInvalid={!!errors.initial_balance}
                    disabled={!!selectedAccount}
                  />
                  <Form.Text className="text-muted">
                    {selectedAccount ? 'El saldo inicial no se puede modificar después de crear la cuenta' : 'Saldo inicial de la cuenta'}
                  </Form.Text>
                  {errors.initial_balance && <Form.Control.Feedback type="invalid">{errors.initial_balance}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <div>
                    <Form.Check
                      type="switch"
                      id="is_active"
                      label="Cuenta Activa"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    <Form.Text className="text-muted">
                      Solo las cuentas activas aparecerán en los listados de selección
                    </Form.Text>
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseForm}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={saveMutation.isLoading}
            >
              {saveMutation.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                selectedAccount ? 'Actualizar' : 'Crear'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default BankAccounts
